import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  Injector,
  Input,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { injectListOrganizationsWithTotalsQuery } from '@models/organization/list-organizations-with-totals.query';

import { ButtonComponent } from '@shared/components/button/button.component';
import { MultiCheckboxSelectComponent } from '@shared/components/multi-checkbox-select-dropdown/multi-checkbox-select-dropdown.component';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { listOrganizationsQuery } from '@shared/services/gql.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';

@Component({
  imports: [MultiCheckboxSelectComponent, ButtonComponent, ClickOutsideDirective],
  selector: 'aux-multiple-vendor-select',
  styles: [
    `
      :host ::ng-deep .multi-vendor-select {
        .ng-dropdown-panel {
          width: auto !important;
          max-width: 550px;
          min-width: 100%;
          max-height: 400px; /* to fix double scrollbar increased default menu height */
        }
      }
    `,
  ],
  templateUrl: './multiple-vendor-select.component.html',
})
export class MultipleVendorSelectComponent implements AfterViewInit {
  injector = inject(Injector);
  vendorQuery = inject(OrganizationQuery);

  listVendorsQuery = injectListOrganizationsWithTotalsQuery({
    enabled: signal(true),
  });

  viewVendorIDs = signal<string[]>([]);

  options = computed(() => {
    let data = this.listVendorsQuery.data() || [];

    if (this.excludeNoBudgetVendors()) {
      data = data.filter((item) => !!item.current_budget_versions.length);
    }

    return data.map((item) => {
      const disabledTooltip = this.getDisabledTooltip(item);

      return {
        disabled: !!disabledTooltip,
        label: item.name,
        tooltip: disabledTooltip,
        value: item.id,
      };
    });
  });

  appliedSelectedVendorIDs = model<string[]>([]);

  applySelectionOnButton = input<boolean>(true);
  disableManualForecastVendors = input<boolean>(false);
  disableNoBudgetVendors = input<boolean>(false);
  excludeNoBudgetVendors = input<boolean>(false);
  itemsToDisplay = input<number>(2);

  applySelection = output<string[]>();

  multiCheckboxSelect = viewChild<MultiCheckboxSelectComponent>('multiCheckboxSelect');

  @Input() isRequired = true;
  @Input() placeholder = 'Select Vendor Contract';

  ngAfterViewInit(): void {
    // set the initial value of viewVendorIDs to appliedSelectedVendorIDs
    this.viewVendorIDs.set(this.appliedSelectedVendorIDs());

    effect(
      () => {
        if (!this.applySelectionOnButton()) {
          this.applyChanges(this.viewVendorIDs());
        }
      },
      { injector: this.injector }
    );
  }

  applyChanges(vendors: string[]) {
    this.appliedSelectedVendorIDs.set(vendors);
    this.applySelection.emit(vendors);

    if (this.applySelectionOnButton()) {
      this.multiCheckboxSelect()?.select()?.close();
    }
  }

  closeOnSelect() {
    // reset selected vendors in ui
    this.viewVendorIDs.set(this.appliedSelectedVendorIDs());
  }

  getDisabledTooltip(item: listOrganizationsQuery): string | undefined {
    if (this.disableNoBudgetVendors() && item.current_budget_versions.length === 0) {
      return 'No budget data found for this Vendor Contract';
    }

    if (
      this.disableManualForecastVendors() &&
      !!this.vendorQuery.getPrimaryBudgetVersion(item.id)?.manual_forecast
    ) {
      return 'This Vendor Contract has a manual forecast';
    }

    return undefined;
  }
}
